import React from 'react';
import { ArrowRight, Briefcase, Heart, GraduationCap, ShoppingBag, Zap, Cpu, Leaf, Building2, Plane, Code } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { useNavigate } from 'react-router-dom';

const Industries = () => {
  const navigate = useNavigate();

  const industries = [
    { icon: Briefcase, name: 'Fintech', description: 'Payment systems, neobanks, lending platforms, and financial infrastructure', color: 'from-blue-500 to-indigo-600', bgColor: 'from-blue-50 to-indigo-50' },
    { icon: Heart, name: 'Healthcare', description: 'Digital health, biotech, medical devices, and healthcare services', color: 'from-rose-500 to-pink-600', bgColor: 'from-rose-50 to-pink-50' },
    { icon: GraduationCap, name: 'EdTech', description: 'Online learning platforms, skill development, and education technology', color: 'from-amber-500 to-orange-600', bgColor: 'from-amber-50 to-orange-50' },
    { icon: ShoppingBag, name: 'E-Commerce', description: 'D2C brands, marketplaces, and retail technology platforms', color: 'from-purple-500 to-violet-600', bgColor: 'from-purple-50 to-violet-50' },
    { icon: Zap, name: 'SaaS', description: 'B2B software, enterprise tools, and cloud-native platforms', color: 'from-emerald-500 to-green-600', bgColor: 'from-emerald-50 to-green-50' },
    { icon: Cpu, name: 'AI & ML', description: 'Artificial intelligence, machine learning, and deep tech innovations', color: 'from-cyan-500 to-teal-600', bgColor: 'from-cyan-50 to-teal-50' },
    { icon: Leaf, name: 'CleanTech', description: 'Renewable energy, sustainability, and environmental technology', color: 'from-green-500 to-emerald-600', bgColor: 'from-green-50 to-emerald-50' },
    { icon: Building2, name: 'PropTech', description: 'Real estate technology, construction tech, and smart buildings', color: 'from-slate-500 to-gray-600', bgColor: 'from-slate-50 to-gray-50' },
    { icon: Plane, name: 'Travel & Hospitality', description: 'Travel platforms, hospitality tech, and tourism innovations', color: 'from-sky-500 to-blue-600', bgColor: 'from-sky-50 to-blue-50' },
    { icon: Code, name: 'Web3 & Blockchain', description: 'Crypto, DeFi, NFTs, and blockchain infrastructure', color: 'from-violet-500 to-purple-600', bgColor: 'from-violet-50 to-purple-50' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <section className="relative py-24 bg-gradient-to-br from-emerald-600 via-green-600 to-emerald-700 overflow-hidden">
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              Industries We <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-200 to-emerald-100">Serve</span>
            </h1>
            <p className="text-xl text-green-50 leading-relaxed">
              Deep sector expertise across the most dynamic industries. We speak your investors' language because we understand your market.
            </p>
          </div>
        </div>
      </section>

      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-7xl mx-auto">
            {industries.map((industry, index) => (
              <Card key={index} className="border-2 border-gray-100 hover:border-emerald-300 transition-all duration-300 hover:shadow-xl hover:-translate-y-2 group cursor-pointer overflow-hidden" data-testid={`industry-card-${index}`}>
                <div className={`h-2 bg-gradient-to-r ${industry.color}`}></div>
                <CardContent className={`p-8 bg-gradient-to-br ${industry.bgColor}`}>
                  <div className={`w-16 h-16 bg-gradient-to-br ${industry.color} rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300 shadow-lg`}>
                    <industry.icon className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">{industry.name}</h3>
                  <p className="text-gray-600 leading-relaxed">{industry.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-16">
            <p className="text-lg text-gray-600 mb-6">Don't see your industry? We work across many more sectors.</p>
            <Button size="lg" onClick={() => navigate('/contact')} data-testid="industries-discuss-btn"
              className="bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 text-white px-12 py-6 shadow-xl transition-all duration-300 hover:scale-105">
              Discuss Your Industry<ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      <section className="py-20 bg-gradient-to-r from-emerald-600 to-green-600">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">Sector-Specific Expertise Matters</h2>
          <p className="text-xl text-green-50 mb-8 max-w-2xl mx-auto">
            We know which investors are active in your space, what they're looking for, and how to position your startup for success.
          </p>
          <Button size="lg" onClick={() => navigate('/contact')} data-testid="industries-cta-btn"
            className="bg-white text-emerald-700 hover:bg-green-50 text-lg px-12 py-6 shadow-2xl hover:shadow-white/50 transition-all duration-300 hover:scale-105 font-bold">
            Connect With Us<ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>
    </div>
  );
};

export default Industries;
